import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  FormControl,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import {
  AdditionalStatusCodeEnum,
  getAdditionalCostStatusName,
} from "../../common/EntitiesManagement";
import Combobox from "../../components/common/Combobox";
import localize from "../../common/localize";
import useGlobalContext from "../../hooks/useGlobalContext";
import { MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../common/utils";

export default function AddOrEditAdditionalCost({
  model,
  setModel,
  setStatus,
}) {
  const { ADDITIONAL_COST_TYPE_ENDPOINT, config } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [selectedStatus, setSelectedStatus] = useState({
    Id: model.Status,
    Name: getAdditionalCostStatusName(model.Status),
  });
  const [additionalCostTypes, setAdditionalCostTypes] = useState([]);

  const getAdditionalCostTypes = async () => {
    const obtainedAdditionalCostTypes = await axios.get(
      `${ADDITIONAL_COST_TYPE_ENDPOINT}`,
      config
    );

    if (
      obtainedAdditionalCostTypes?.data?.Ok &&
      obtainedAdditionalCostTypes?.data?.Data?.length > 0
    ) {
      setAdditionalCostTypes(obtainedAdditionalCostTypes?.data?.Data);
    }
  };


  useEffect(() => {
    getAdditionalCostTypes();
  }, []);

  useEffect(() => {
    if (selectedStatus) {
      setModel({
        ...model,
        Status: selectedStatus.Id,
      });
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (model.Observation) {
      setDescriptionCount(model.Observation.length);
    }
  }, [model?.Observation]);

  const handleChange = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleAdditionalCostTypeChange = (value) => {
    setModel({
      ...model,
      AdditionalCostTypeId: !value ? 0 : value.Id,
      AdditionalCostType: !value ? null : value,
      Value: value.DefaultValue,
    });
  };

  const handleAdditionalCostStatusChange = (value) => {
    if (!value) {
      setSelectedStatus({
        Id: 0,
        Name: getAdditionalCostStatusName(0),
      });
      return;
    }

    setSelectedStatus({
      Id: value.Id,
      Name: value.Name,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full mt-2">
        <div className="flex flex-wrap mb-6">
          <FormControl required fullWidth>
            <Combobox
              isRequired
              disabled={setStatus || additionalCostTypes.length === 0}
              id="AdditionalCostTypeId"
              fieldLabel={localize.additional_cost_type}
              fieldOptions={additionalCostTypes ?? []}
              fieldValue={model.AdditionalCostType}
              handleValueChange={handleAdditionalCostTypeChange}
            />
          </FormControl>
        </div>
        <div className="flex flex-wrap mb-6">
          <TextField
            disabled={setStatus}
            type="number"
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            value={model.Value}
            onChange={handleChange}
            size="small"
            id="Value"
            name="Value"
            label={localize.value}
            variant="outlined"
            fullWidth
          />
        </div>

        {model.value < 0 && (
          <div className="w-full mb-6">
            <Alert className="w-full" severity="warning">
              <label className="text-xs">
                {localize.warning_value_discounted}
              </label>
            </Alert>
          </div>
        )}

        <div className="flex flex-wrap mb-6">
          <FormControl required fullWidth>
            <TextField
              disabled={setStatus}
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                style: {
                  fontSize: "0.8em",
                },
                maxLength: maximumCharactersForDescription,
              }}
              size="small"
              fullWidth
              multiline
              rows={3}
              id="outlined-basic"
              name="Observation"
              label={localize.observations}
              variant="outlined"
              onChange={handleChange}
              value={model.Description}
              helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
            />
          </FormControl>
        </div>

        {setStatus && (
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Card
              variant="outlined"
              onClick={() =>
                handleAdditionalCostStatusChange({
                  Id: AdditionalStatusCodeEnum.Approved,
                  Name: getAdditionalCostStatusName(AdditionalStatusCodeEnum.Approved),
                })
              }
              sx={{
                flex: 1,
                border:
                  selectedStatus?.Id === AdditionalStatusCodeEnum.Approved
                    ? "2px solid #2E7D32"
                    : "1px solid #E0E0E0",
                backgroundColor:
                  selectedStatus?.Id === AdditionalStatusCodeEnum.Approved
                    ? "#E8F5E9"
                    : "transparent",
                cursor: "pointer",
                borderRadius: "8px",
              }}
            >
              <CardActionArea
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Radio
                  size="small"
                  checked={selectedStatus?.Id === AdditionalStatusCodeEnum.Approved}
                  sx={{
                    color: "#2E7D32",
                    "&.Mui-checked": { color: "#2E7D32" },
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#2E7D32" }}
                >
                  {localize.approve}
                </Typography>
              </CardActionArea>
            </Card>

            <Card
              variant="outlined"
              onClick={() =>
                handleAdditionalCostStatusChange({
                  Id: AdditionalStatusCodeEnum.Rejected,
                  Name: getAdditionalCostStatusName(AdditionalStatusCodeEnum.Rejected),
                })
              }
              sx={{
                flex: 1,
                border:
                  selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected
                    ? "2px solid #C62828"
                    : "1px solid #E0E0E0",
                backgroundColor:
                  selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected
                    ? "#FFEBEE"
                    : "transparent",
                cursor: "pointer",
                borderRadius: "8px",
              }}
            >
              <CardActionArea
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Radio
                  size="small"
                  checked={selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected}
                  sx={{
                    color: "#C62828",
                    "&.Mui-checked": { color: "#C62828" },
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#C62828" }}
                >
                  {localize.reject}
                </Typography>
              </CardActionArea>
            </Card>
          </Box>
        )}

        {setStatus &&
          (selectedStatus?.Id === AdditionalStatusCodeEnum.Approved ? (
            <Alert severity="info" className="w-full">
              <label className="text-xs">
                {localize.info_approved_additional_cost}
              </label>
            </Alert>
          ) : selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected ? (
            <Alert className="w-full" severity="warning">
              <label className="text-xs">
                {localize.warning_rejected_additional_cost}
              </label>
            </Alert>
          ) : (
            <></>
          ))}

        {setStatus && selectedStatus?.Id === AdditionalStatusCodeEnum.Rejected && (
          <div className="flex flex-wrap mb-6 mt-3">
            <FormControl required fullWidth>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={{
                  style: {
                    fontSize: "0.8em",
                  },
                  maxLength: maximumCharactersForDescription,
                }}
                size="small"
                fullWidth
                multiline
                rows={3}
                name="Observation"
                label={localize.observations}
                variant="outlined"
                onChange={handleChange}
                value={model.Observation}
                helperText={
                  !model.Observation || model.Observation.length === 0
                    ? localize.error_empty_field
                    : `${model.Observation.length}/${maximumCharactersForDescription}`
                }
                error={!model.Observation || model.Observation.length === 0}
              />
            </FormControl>
          </div>
        )}
      </form>
    </div>
  );
}
